import { supabase } from '../lib/supabase';

export type TrackingEventType = 
  | 'landing_page_accessed'
  | 'property_opened'
  | 'configurator_accessed'
  | 'order_summary_accessed'
  | 'checkout_initiated'
  | 'payment_page_accessed'
  | 'purchase_completed'
  | 'email_opened'
  | 'email_clicked'
  | 'email_sent';

interface TrackingEventData {
  eventType: TrackingEventType;
  propertyId?: string;
  campaignId?: string;
  accessCode?: string;
  sessionId?: string;
  eventData?: Record<string, any>;
  referrer?: string;
  checkoutSessionId?: string;
  paymentIntentId?: string;
  orderAmount?: number; // in cents
}

interface PropertyTrackingData {
  propertyId: string;
  campaignId?: string;
  accessCode?: string;
}

class CampaignTrackingService {
  private sessionId: string;
  private isDebugMode: boolean = false;
  private recentEvents: Map<string, number> = new Map(); // Store recent event timestamps

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.isDebugMode = import.meta.env.DEV || false;
  }

  private getOrCreateSessionId(): string {
    // Try to get existing session ID from sessionStorage
    let sessionId = sessionStorage.getItem('tracking_session_id');
    
    if (!sessionId) {
      // Generate new session ID
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('tracking_session_id', sessionId);
    }
    
    return sessionId;
  }

  private getUserAgent(): string {
    return navigator.userAgent || '';
  }

  private getReferrer(): string {
    return document.referrer || '';
  }

  private async getLocationData() {
    try {
      // Try to get basic location info (this could be enhanced with a geolocation service)
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return {
        timezone,
        language: navigator.language
      };
    } catch (error) {
      console.warn('Could not get location data:', error);
      return {};
    }
  }

  private log(message: string, data?: any) {
    if (this.isDebugMode) {
      console.log(`🎯 [CampaignTracking] ${message}`, data);
    }
  }

  /**
   * Store original campaign code for persistence across sessions
   */
  private storeOriginalCampaignCode(accessCode: string): void {
    if (accessCode && !accessCode.startsWith('IMG')) {
      localStorage.setItem('original_campaign_code', accessCode);
      console.log(`🎯 [PERSIST] Stored original campaign code: ${accessCode}`);
    }
  }

  /**
   * Get stored original campaign code
   */
  private getStoredCampaignCode(): string | null {
    const stored = localStorage.getItem('original_campaign_code');
    if (stored) {
      console.log(`🎯 [PERSIST] Retrieved stored campaign code: ${stored}`);
    }
    return stored;
  }

  /**
   * Clear stored campaign code (for testing or when starting fresh)
   */
  public clearStoredCampaignCode(): void {
    localStorage.removeItem('original_campaign_code');
    console.log(`🎯 [PERSIST] Cleared stored campaign code`);
  }

  /**
   * Check if we should skip this event due to recent duplicate
   */
  private shouldSkipDuplicateEvent(
    eventType: TrackingEventType, 
    propertyId: string, 
    accessCode?: string,
    windowMs: number = 10000 // 10 seconds
  ): boolean {
    const identifier = accessCode || propertyId;
    const eventKey = `${eventType}:${identifier}`;
    const now = Date.now();
    const lastEventTime = this.recentEvents.get(eventKey);

    if (lastEventTime && (now - lastEventTime) < windowMs) {
      const timeDiff = now - lastEventTime;
      // Always show skip messages (not just in debug mode)
      console.log(`🔄 [DEDUP] Skipping duplicate ${eventType} for ${identifier} (${timeDiff}ms ago, within ${windowMs}ms window)`);
      this.log(`🔄 Skipping duplicate event ${eventType} for ${identifier} (${timeDiff}ms ago)`);
      return true;
    }

    // Store this event timestamp
    this.recentEvents.set(eventKey, now);
    
    // Always log when we're allowing an event through
    console.log(`✅ [DEDUP] Allowing ${eventType} for ${identifier} (last event: ${lastEventTime ? now - lastEventTime + 'ms ago' : 'never'})`);
    
    // Clean up old entries (older than 5 minutes)
    for (const [key, timestamp] of this.recentEvents.entries()) {
      if (now - timestamp > 300000) { // 5 minutes
        this.recentEvents.delete(key);
      }
    }

    return false;
  }

  /**
   * Track any campaign event
   */
  async trackEvent(data: TrackingEventData): Promise<boolean> {
    try {
      this.log(`Tracking event: ${data.eventType}`, data);

      const locationData = await this.getLocationData();

      const eventRecord = {
        event_type: data.eventType,
        property_id: data.propertyId || null,
        campaign_id: data.campaignId || null,
        access_code: data.accessCode || null,
        session_id: data.sessionId || this.sessionId,
        user_agent: this.getUserAgent(),
        event_data: data.eventData || null,
        referrer: data.referrer || this.getReferrer(),
        checkout_session_id: data.checkoutSessionId || null,
        payment_intent_id: data.paymentIntentId || null,
        order_amount: data.orderAmount || null,
        ...locationData
      };

      const { error } = await supabase
        .from('campaign_events')
        .insert([eventRecord]);

      if (error) {
        console.error('Failed to track event:', error);
        return false;
      }

      this.log(`Successfully tracked ${data.eventType} event`);
      return true;
    } catch (error) {
      console.error('Error tracking event:', error);
      return false;
    }
  }

  /**
   * Track when property is opened (legacy)
   */
  async trackPropertyOpened(data: PropertyTrackingData): Promise<boolean> {
    console.log(`🎯 [TRACK] trackPropertyOpened called:`, {
      propertyId: data.propertyId,
      accessCode: data.accessCode,
      campaignId: data.campaignId,
      timestamp: new Date().toISOString()
    });

    // Store original campaign code for persistence
    if (data.accessCode) {
      this.storeOriginalCampaignCode(data.accessCode);
    }

    // Check for duplicate events
    if (this.shouldSkipDuplicateEvent('property_opened', data.propertyId, data.accessCode)) {
      console.log(`🔄 [TRACK] trackPropertyOpened SKIPPED due to deduplication`);
      return true; // Return true to not break the flow
    }

    console.log(`✅ [TRACK] trackPropertyOpened proceeding with event tracking`);

    await this.trackEvent({
      eventType: 'property_opened',
      propertyId: data.propertyId,
      campaignId: data.campaignId,
      accessCode: data.accessCode
    });

    console.log(`✅ [TRACK] trackPropertyOpened proceeding with database update`);

    // Also update the scraped_properties table for quick access
    const result = await this.updatePropertyTracking(data.propertyId, 'property_opened', undefined, data.accessCode);
    
    console.log(`✅ [TRACK] trackPropertyOpened completed:`, {
      result,
      propertyId: data.propertyId,
      accessCode: data.accessCode
    });

    return result;
  }

  /**
   * Track when landing page is accessed via access code
   */
  async trackLandingPageAccessed(data: PropertyTrackingData): Promise<boolean> {
    // Store original campaign code for persistence
    if (data.accessCode) {
      this.storeOriginalCampaignCode(data.accessCode);
    }

    // Check for duplicate events
    if (this.shouldSkipDuplicateEvent('landing_page_accessed', data.propertyId, data.accessCode)) {
      return true; // Return true to not break the flow
    }

    await this.trackEvent({
      eventType: 'landing_page_accessed',
      propertyId: data.propertyId,
      campaignId: data.campaignId,
      accessCode: data.accessCode
    });

    // Also update the scraped_properties table for quick access
    return this.updatePropertyTracking(data.propertyId, 'landing_page_accessed', undefined, data.accessCode);
  }

  /**
   * Track when configurator is accessed
   */
  async trackConfiguratorAccessed(data: PropertyTrackingData): Promise<boolean> {
    // Store original campaign code for persistence
    if (data.accessCode) {
      this.storeOriginalCampaignCode(data.accessCode);
    }

    // For temporary tokens, try to use stored original campaign code
    let effectiveAccessCode = data.accessCode;
    if (data.accessCode?.startsWith('IMG')) {
      const storedCode = this.getStoredCampaignCode();
      if (storedCode) {
        effectiveAccessCode = storedCode;
        console.log(`🎯 [PERSIST] Using stored campaign code ${storedCode} instead of temporary token ${data.accessCode}`);
      }
    }

    // Check for duplicate events using effective access code
    if (this.shouldSkipDuplicateEvent('configurator_accessed', data.propertyId, effectiveAccessCode)) {
      return true; // Return true to not break the flow
    }

    await this.trackEvent({
      eventType: 'configurator_accessed',
      propertyId: data.propertyId,
      campaignId: data.campaignId,
      accessCode: effectiveAccessCode
    });

    // Also update the scraped_properties table for quick access using effective access code
    return this.updatePropertyTracking(data.propertyId, 'configurator_accessed', undefined, effectiveAccessCode);
  }

  /**
   * Track when order summary/details page is accessed
   */
  async trackOrderSummaryAccessed(data: PropertyTrackingData): Promise<boolean> {
    // Store original campaign code for persistence
    if (data.accessCode) {
      this.storeOriginalCampaignCode(data.accessCode);
    }

    // For temporary tokens, try to use stored original campaign code
    let effectiveAccessCode = data.accessCode;
    if (data.accessCode?.startsWith('IMG')) {
      const storedCode = this.getStoredCampaignCode();
      if (storedCode) {
        effectiveAccessCode = storedCode;
        console.log(`🎯 [PERSIST] Using stored campaign code ${storedCode} for order tracking instead of temporary token ${data.accessCode}`);
      }
    }

    // Check for duplicate events using effective access code
    if (this.shouldSkipDuplicateEvent('order_summary_accessed', data.propertyId, effectiveAccessCode)) {
      return true; // Return true to not break the flow
    }
    
    await this.trackEvent({
      eventType: 'order_summary_accessed',
      propertyId: data.propertyId,
      campaignId: data.campaignId,
      accessCode: effectiveAccessCode
    });

    // Also update the scraped_properties table for quick access using effective access code
    return this.updatePropertyTracking(data.propertyId, 'order_summary_accessed', undefined, effectiveAccessCode);
  }

  /**
   * Track when checkout is initiated
   */
  async trackCheckoutInitiated(data: PropertyTrackingData & { checkoutSessionId?: string }): Promise<boolean> {
    // Store original campaign code for persistence
    if (data.accessCode) {
      this.storeOriginalCampaignCode(data.accessCode);
    }

    // For temporary tokens, try to use stored original campaign code
    let effectiveAccessCode = data.accessCode;
    if (data.accessCode?.startsWith('IMG')) {
      const storedCode = this.getStoredCampaignCode();
      if (storedCode) {
        effectiveAccessCode = storedCode;
        console.log(`🎯 [PERSIST] Using stored campaign code ${storedCode} for checkout tracking instead of temporary token ${data.accessCode}`);
      }
    }

    // Check for duplicate events using effective access code
    if (this.shouldSkipDuplicateEvent('checkout_initiated', data.propertyId, effectiveAccessCode)) {
      return true; // Return true to not break the flow
    }

    await this.trackEvent({
      eventType: 'checkout_initiated',
      propertyId: data.propertyId,
      campaignId: data.campaignId,
      accessCode: effectiveAccessCode,
      checkoutSessionId: data.checkoutSessionId
    });

    return this.updatePropertyTracking(data.propertyId, 'checkout_initiated', undefined, effectiveAccessCode);
  }

  /**
   * Track when payment page is accessed
   */
  async trackPaymentPageAccessed(data: PropertyTrackingData): Promise<boolean> {
    // Store original campaign code for persistence
    if (data.accessCode) {
      this.storeOriginalCampaignCode(data.accessCode);
    }

    // For temporary tokens, try to use stored original campaign code
    let effectiveAccessCode = data.accessCode;
    if (data.accessCode?.startsWith('IMG')) {
      const storedCode = this.getStoredCampaignCode();
      if (storedCode) {
        effectiveAccessCode = storedCode;
        console.log(`🎯 [PERSIST] Using stored campaign code ${storedCode} for payment tracking instead of temporary token ${data.accessCode}`);
      }
    }

    // Check for duplicate events using effective access code
    if (this.shouldSkipDuplicateEvent('payment_page_accessed', data.propertyId, effectiveAccessCode)) {
      return true; // Return true to not break the flow
    }

    await this.trackEvent({
      eventType: 'payment_page_accessed',
      propertyId: data.propertyId,
      campaignId: data.campaignId,
      accessCode: effectiveAccessCode
    });

    return this.updatePropertyTracking(data.propertyId, 'payment_page_accessed', undefined, effectiveAccessCode);
  }

  /**
   * Track when purchase is completed
   */
  async trackPurchaseCompleted(data: PropertyTrackingData & { 
    orderAmount?: number;
    paymentIntentId?: string;
  }): Promise<boolean> {
    await this.trackEvent({
      eventType: 'purchase_completed',
      propertyId: data.propertyId,
      campaignId: data.campaignId,
      accessCode: data.accessCode,
      paymentIntentId: data.paymentIntentId,
      orderAmount: data.orderAmount
    });

    return this.updatePropertyTracking(data.propertyId, 'purchase_completed', data.orderAmount, data.accessCode);
  }

  /**
   * Check if an email should be treated as a test email (to prevent tracking)
   */
  private isTestEmail(recipientEmail?: string, agentName?: string): boolean {
    const email = (recipientEmail || '').toLowerCase();
    const name = (agentName || '').toLowerCase();
    
    // Check for test patterns in email
    // NOTE: To test email tracking, use an email that doesn't contain 'test' or 'demo'
    // Example: use 'agent@realestate.com' or 'john.smith@example.com'
    if (email.includes('test') || 
        email.includes('demo') || 
        email === 'thomkleist@gmail.com' || 
        email === 'thomas@mshstudio.ai') {
      return true;
    }
    
    // Check for test patterns in name
    if (name.includes('test') || 
        name.includes('demo') || 
        name === 'thomas kleist') {
      return true;
    }
    
    return false;
  }

  /**
   * Track when outreach email is sent
   */
  async trackEmailSent(data: PropertyTrackingData & { 
    emailType?: string;
    recipientEmail?: string;
    agentName?: string;
  }): Promise<boolean> {
    // Skip tracking for test emails
    if (this.isTestEmail(data.recipientEmail, data.agentName)) {
      this.log(`Skipping tracking for test email: ${data.recipientEmail}`);
      return true; // Return true so the email process continues normally
    }

    await this.trackEvent({
      eventType: 'email_sent',
      propertyId: data.propertyId,
      campaignId: data.campaignId,
      accessCode: data.accessCode,
      eventData: {
        emailType: data.emailType || 'anniversary_outreach',
        recipientEmail: data.recipientEmail
      }
    });

    return this.updatePropertyOutreachStatus(data.propertyId, 'sent');
  }

  /**
   * Track when outreach email is opened
   */
  async trackEmailOpened(data: PropertyTrackingData): Promise<boolean> {
    await this.trackEvent({
      eventType: 'email_opened',
      propertyId: data.propertyId,
      campaignId: data.campaignId,
      accessCode: data.accessCode
    });

    return this.updatePropertyOutreachStatus(data.propertyId, 'opened');
  }

  /**
   * Track when outreach email is clicked
   */
  async trackEmailClicked(data: PropertyTrackingData): Promise<boolean> {
    await this.trackEvent({
      eventType: 'email_clicked',
      propertyId: data.propertyId,
      campaignId: data.campaignId,
      accessCode: data.accessCode
    });

    return this.updatePropertyOutreachStatus(data.propertyId, 'clicked');
  }

  /**
   * Update property tracking fields in scraped_properties table
   */
  private async updatePropertyTracking(
    propertyId: string, 
    eventType: TrackingEventType, 
    orderAmount?: number,
    accessCode?: string
  ): Promise<boolean> {
    try {
      const now = new Date().toISOString();

      // Check if this is a real campaign access code vs. temporary token
      const isTemporaryToken = accessCode?.startsWith('IMG');
      const isRealCampaignCode = accessCode && !isTemporaryToken;

      console.log(`�� [TRACK] Processing tracking update:`, {
        propertyId,
        eventType,
        accessCode,
        isTemporaryToken,
        isRealCampaignCode
      });

      // Only update scraped_properties for real campaign access codes
      if (isRealCampaignCode) {
        this.log(`Updating scraped_properties by access_code: ${accessCode}`);
        
        // First get the current count to increment it
        const { data: currentData, error: fetchError } = await supabase
          .from('scraped_properties')
          .select(`
            landing_page_accessed_count,
            configurator_opened_count,
            order_summary_accessed_count,
            checkout_initiated_count,
            payment_page_accessed_count
          `)
          .eq('access_code', accessCode)
          .single();

        if (fetchError) {
          this.log(`⚠️ Error fetching current counts for access_code: ${accessCode}`, fetchError);
          console.log(`⚠️ [TRACK] Campaign property not found in scraped_properties for access_code: ${accessCode}`);
          return false;
        }

        let updateData: any = {
          last_activity_at: now
        };

        // Calculate new counts based on event type
        switch (eventType) {
          case 'landing_page_accessed':
            updateData.landing_page_accessed_at = now;
            updateData.landing_page_accessed_count = (currentData?.landing_page_accessed_count || 0) + 1;
            console.log(`🎯 [DB UPDATE] landing_page_accessed - updating landing_page_accessed_count from ${currentData?.landing_page_accessed_count || 0} to ${updateData.landing_page_accessed_count}`);
            break;
          case 'property_opened':
          case 'configurator_accessed':
            updateData.configurator_opened_at = now;
            updateData.configurator_opened_count = (currentData?.configurator_opened_count || 0) + 1;
            console.log(`🎯 [DB UPDATE] ${eventType} - updating configurator_opened_count from ${currentData?.configurator_opened_count || 0} to ${updateData.configurator_opened_count}`);
            break;
          case 'order_summary_accessed':
            updateData.order_summary_accessed_at = now;
            updateData.order_summary_accessed_count = (currentData?.order_summary_accessed_count || 0) + 1;
            console.log(`🎯 [DB UPDATE] order_summary_accessed - updating order_summary_accessed_count from ${currentData?.order_summary_accessed_count || 0} to ${updateData.order_summary_accessed_count}`);
            break;
          case 'checkout_initiated':
            updateData.checkout_initiated_at = now;
            updateData.checkout_initiated_count = (currentData?.checkout_initiated_count || 0) + 1;
            console.log(`🎯 [DB UPDATE] checkout_initiated - updating checkout_initiated_count from ${currentData?.checkout_initiated_count || 0} to ${updateData.checkout_initiated_count}`);
            break;
          case 'payment_page_accessed':
            updateData.payment_page_accessed_at = now;
            updateData.payment_page_accessed_count = (currentData?.payment_page_accessed_count || 0) + 1;
            console.log(`🎯 [DB UPDATE] payment_page_accessed - updating payment_page_accessed_count from ${currentData?.payment_page_accessed_count || 0} to ${updateData.payment_page_accessed_count}`);
            break;
          case 'purchase_completed':
            updateData.purchase_completed_at = now;
            if (orderAmount) {
              updateData.purchase_amount = orderAmount;
            }
            console.log(`🎯 [DB UPDATE] purchase_completed - updating purchase_completed_at and purchase_amount: ${orderAmount || 'no amount'}`);
            break;
        }

        console.log(`🔄 [TRACK] updateData before update:`, updateData);
        const { data: scrapedData, error: scrapedError } = await supabase
          .from('scraped_properties')
          .update(updateData)
          .eq('access_code', accessCode)
          .select('id');

        if (!scrapedError && scrapedData && scrapedData.length > 0) {
          this.log(`✅ Updated scraped_properties for access_code: ${accessCode}`);
          console.log(`✅ [TRACK] Campaign tracking updated for access_code: ${accessCode}`);
          return true;
        } else {
          this.log(`⚠️ Failed to update scraped_properties for access_code: ${accessCode}`, scrapedError);
          console.log(`⚠️ [TRACK] Failed to update campaign tracking for access_code: ${accessCode}`);
          return false;
        }
      } else {
        // No real campaign access code - this is regular flow or temporary token
        if (isTemporaryToken) {
          this.log(`Temporary token detected - only tracking to campaign_events table (token: ${accessCode})`);
          console.log(`ℹ️ [TRACK] Temporary token ${accessCode} - skipping campaign counter updates`);
        } else {
          this.log(`Regular flow property - only tracking to campaign_events table (propertyId: ${propertyId})`);
          console.log(`ℹ️ [TRACK] Regular flow property - campaign tracking counters not available for property_searches`);
        }
        return true; // Event was logged to campaign_events, which is sufficient for regular flow
      }
    } catch (error) {
      console.error('Error updating property tracking:', error);
      return false;
    }
  }

  /**
   * Update property outreach status in scraped_properties table
   */
  private async updatePropertyOutreachStatus(
    propertyId: string, 
    status: 'sent' | 'opened' | 'clicked'
  ): Promise<boolean> {
    try {
      const now = new Date().toISOString();
      const updateData: any = {
        last_activity_at: now,
        outreach_status: status,
        outreach_sent_at: status === 'sent' ? now : null,
        outreach_opened_at: status === 'opened' ? now : null,
        outreach_clicked_at: status === 'clicked' ? now : null
      };

      const { error } = await supabase
        .from('scraped_properties')
        .update(updateData)
        .eq('id', propertyId);

      if (error) {
        console.warn('Failed to update outreach status:', error);
        return false;
      }

      this.log(`Updated outreach status for ${propertyId} to ${status}`);
      return true;
    } catch (error) {
      console.error('Error updating outreach status:', error);
      return false;
    }
  }

  /**
   * Get campaign property for access code - returns campaign and property info
   */
  async getCampaignPropertyInfo(accessCode: string): Promise<PropertyTrackingData | null> {
    try {
      // First check scraped_properties (campaign properties)
      const { data: scrapedProperty, error: scrapedError } = await supabase
        .from('scraped_properties')
        .select('id, campaign_id, access_code')
        .eq('access_code', accessCode)
        .maybeSingle();

      if (!scrapedError && scrapedProperty) {
        return {
          propertyId: scrapedProperty.id,
          campaignId: scrapedProperty.campaign_id,
          accessCode: scrapedProperty.access_code
        };
      }

      // If not found, check property_searches (regular properties) 
      const { data: propertySearch, error: propertyError } = await supabase
        .from('property_searches')
        .select('id, property_token')
        .eq('property_token', accessCode)
        .maybeSingle();

      if (!propertyError && propertySearch) {
        return {
          propertyId: propertySearch.id,
          accessCode: accessCode
        };
      }

      this.log(`No property found for access code: ${accessCode}`);
      return null;
    } catch (error) {
      console.error('Error getting campaign property info:', error);
      return null;
    }
  }

  /**
   * Get campaign analytics
   */
  async getCampaignAnalytics(campaignId: string, daysBack: number = 30) {
    try {
      const { data, error } = await supabase.rpc('get_campaign_analytics_v3', {
        campaign_uuid: campaignId,
        days_back: daysBack
      });

      if (error) {
        console.error('Failed to get campaign analytics:', error);
        return null;
      }

      return data?.[0] || null;
    } catch (error) {
      console.error('Error getting campaign analytics:', error);
      return null;
    }
  }

  /**
   * Reset session (useful for testing)
   */
  resetSession(): void {
    sessionStorage.removeItem('tracking_session_id');
    this.sessionId = this.getOrCreateSessionId();
    this.log('Session reset', { newSessionId: this.sessionId });
  }

  /**
   * Get current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }
}

// Export singleton instance
export const campaignTracking = new CampaignTrackingService();

// Export class for testing